"use client";

import { useEffect } from "react";
import { motion } from "framer-motion";
import { Box, Typography } from "@mui/material";
import { track } from "@/utils/analytics";
import { APP_MAX_WIDTH } from "@/components/PageContent/PageContent";
import { SAFE_AREA_BOTTOM } from "@/contexts/SafeAreaContext";
import { Z_INDEX } from "@/constants/zIndex";
import { useDarkColors } from "@/hooks/useDarkColors";
import { getAssetUrl } from "@/utils/assetUrl";
import {
  TEMP_TEXT_BOLD_XXL,
  TEMP_TEXT_SEMIBOLD_L,
  TEMP_TEXT_SEMIBOLD_XL,
  TEMP_TEXT_MEDIUM_L,
} from "@/constants/tempTypo";
import type { WorldCupCharacter } from "@/stores/worldCupStore";
import { useTranslator } from "@/lang";

interface IntroModalProps {
  characters: WorldCupCharacter[];
  onStart: () => void;
  onSkip: () => void;
}

const EASING: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

export default function IntroModal({
  characters,
  onStart,
  onSkip,
}: IntroModalProps) {
  const c = useDarkColors();
  const { t } = useTranslator();

  useEffect(() => {
    track('worldcup_intro_shown', { candidate_count: characters.length });
  }, [characters.length]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25, ease: EASING }}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: Z_INDEX.MODAL,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
    >
      {/* Backdrop */}
      <Box
        sx={{ position: "absolute", inset: 0, bgcolor: "rgba(0, 0, 0, 0.5)" }}
      />

      {/* BottomSheet card */}
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "tween", duration: 0.3, ease: EASING }}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: APP_MAX_WIDTH,
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            bgcolor: c.bg,
            borderTopLeftRadius: "24px",
            borderTopRightRadius: "24px",
            overflow: "hidden",
          }}
        >
          {/* Handle */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              pt: "12px",
              pb: "4px",
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 4,
                borderRadius: "9999px",
                bgcolor: c.isDark ? "#555555" : "#D9D9D9",
              }}
            />
          </Box>

          {/* Content */}
          <Box
            sx={{
              pt: "12px",
              pb: `calc(20px + ${SAFE_AREA_BOTTOM})`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* Profile slider */}
            <ProfileSlider characters={characters} isDark={c.isDark} />

            {/* Title */}
            <Typography
              sx={{
                ...TEMP_TEXT_BOLD_XXL,
                color: c.fg,
                textAlign: "center",
                mt: "20px",
                mb: "8px",
              }}
            >
              {t("worldcup.intro_title")}
            </Typography>

            {/* Subtitle */}
            <Typography
              sx={{
                ...TEMP_TEXT_MEDIUM_L,
                color: c.fgLow,
                textAlign: "center",
                mb: "28px",
                lineHeight: 1.5,
              }}
            >
              {t("worldcup.intro_subtitle")}
            </Typography>

            {/* Buttons */}
            <Box sx={{ px: "24px", width: "100%" }}>
              <Box
                onClick={onStart}
                sx={{
                  width: "100%",
                  height: 56,
                  borderRadius: "16px",
                  bgcolor: c.primary,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  WebkitTapHighlightColor: "transparent",
                  mb: "10px",
                }}
              >
                <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XL, color: "#FFFFFF" }}>
                  {t("worldcup.intro_start")}
                </Typography>
              </Box>

              <Box
                onClick={onSkip}
                sx={{
                  width: "100%",
                  height: 48,
                  borderRadius: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_L, color: c.fgLower }}>
                  {t("worldcup.intro_skip")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </motion.div>
    </motion.div>
  );
}

function ProfileSlider({
  characters,
  isDark,
}: {
  characters: WorldCupCharacter[];
  isDark: boolean;
}) {
  const imgs = characters
    .map((ch) => ({
      id: ch.id,
      url: getAssetUrl(ch.profileImagePath, "large"),
    }))
    .filter((x) => !!x.url);

  // 양쪽으로 복제해서 끊김 없는 루프
  const doubled = [...imgs, ...imgs];
  const itemSize = 48;
  const gap = 10;
  const totalWidth = imgs.length * (itemSize + gap);

  if (imgs.length === 0) return null;

  return (
    <Box
      sx={{
        width: "100%",
        overflow: "hidden",
        position: "relative",
        height: itemSize + 4,
      }}
    >
      {/* 양쪽 페이드 */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 40,
          height: "100%",
          background: `linear-gradient(to right, ${isDark ? "#121212" : "#FFFFFF"}, transparent)`,
          zIndex: 1,
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 40,
          height: "100%",
          background: `linear-gradient(to left, ${isDark ? "#121212" : "#FFFFFF"}, transparent)`,
          zIndex: 1,
          pointerEvents: "none",
        }}
      />

      <motion.div
        animate={{ x: [0, -totalWidth] }}
        transition={{
          x: {
            duration: imgs.length * 2.5,
            repeat: Infinity,
            ease: "linear",
          },
        }}
        style={{
          display: "flex",
          gap,
          width: "max-content",
        }}
      >
        {doubled.map((img, i) => (
          <Box
            key={`${img.id}-${i}`}
            sx={{
              width: itemSize,
              height: itemSize,
              borderRadius: "50%",
              overflow: "hidden",
              flexShrink: 0,
              border: `2px solid ${isDark ? "rgba(124, 58, 237, 0.3)" : "rgba(75, 0, 204, 0.12)"}`,
            }}
          >
            <Box
              component="img"
              src={img.url}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </Box>
        ))}
      </motion.div>
    </Box>
  );
}
